import { createOrganizationUser, NewUser } from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import {
  LANGUAGE,
  LOCALE_TO_LABEL,
  LOCALES,
  USER_ACCESS,
  USER_ACCESS_TO_LABEL,
  USER_ACCESS_TYPES,
} from "@/constants";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { Organization } from "@/types/Organization";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

export default function InviteUser() {
  const { t } = useLingui();

  // URL state
  const history = useHistory();
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(true);

  // Validation
  const formSchema = z.object({
    email: z.email({
      message: t`Vänligen ange en korrekt e-postadress`,
    }),
    language: z.enum(LOCALES),
    userType: z.enum(USER_ACCESS_TYPES),
  });

  // Form state
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      language: LANGUAGE.SWEDISH,
      userType: USER_ACCESS.USER,
    },
  });

  // Handlers
  function handleOpenChange(open: boolean) {
    if (!open) {
      setIsOpen(false);
      setTimeout(() => {
        history.push(generatePath(ROUTES.SETTINGS_TEAM_ORGID, { orgUuid }));
      }, 300);
    }
  }

  // Server state
  const queryClient = useQueryClient();

  const { mutateAsync: mutateCreateOrganizationUser } = useMutation({
    mutationFn: (values: NewUser) => {
      return createOrganizationUser({
        organization_uuid: orgUuid,
        user: values,
      });
    },
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error();
      }

      postTrackingData(EVENT.INVITE_USER, {
        user_type: res.data?.user_type,
      });

      queryClient.invalidateQueries({
        queryKey: [orgUuid, "users"],
      });
      toast.success(t`Inbjudan skickades`);
      handleOpenChange(false);
    },
    onError: () => {
      toast.error(t`Någonting gick fel när vi skulle skicka inbjudan`);
    },
  });

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = (values) => {
    return mutateCreateOrganizationUser({
      user_type: values.userType,
      language: values.language,
      email: values.email,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Bjud in en kollega</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Lägg till en kollega som ska kunna ta del av omnämnanden,
              mejlrapporter och analyser.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <form id="invite-user-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <FieldGroup>
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "user-edit-email";
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>E-postadress</Trans>
                    </FieldLabel>
                    <Input
                      {...field}
                      id={id}
                      aria-invalid={fieldState.invalid}
                      placeholder="user@example.com"
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="language"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "language";
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Språk</Trans>
                    </FieldLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id={id}
                        className="w-full"
                        aria-invalid={fieldState.invalid}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(LOCALE_TO_LABEL).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {i18n._(label)}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    {fieldState.error && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="userType"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "user-edit-user-type";
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Åtkomstnivå</Trans>
                    </FieldLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                        <SelectValue placeholder={t`Välj roll`} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(USER_ACCESS_TO_LABEL).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {i18n._(label)}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    <FieldDescription>
                      <a
                        href={t`https://help.allears.ai/sv/articles/7896568-vad-innebar-accessnivaerna`}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Trans>Läs mer om åtkomstnivåer</Trans>
                      </a>
                    </FieldDescription>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={form.formState.isSubmitting}
            onClick={() => handleOpenChange(false)}
          >
            <Trans>Avbryt</Trans>
          </Button>

          <Button
            type="submit"
            form="invite-user-form"
            disabled={form.formState.isSubmitting}
            data-testid="user-add-step-1-submit"
          >
            <Trans>Bjud in</Trans>
            {form.formState.isSubmitting && <Spinner />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
